import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Home, MapPin, Building, Users, Settings, User, FileText, Landmark, ExternalLink, Wand2, Image } from 'lucide-react';
import { OnboardingBanner } from '@/components/onboarding/OnboardingBanner';
import { LeadNotificationSettings } from '@/components/admin/LeadNotificationSettings';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Badge } from '@/components/ui/badge';

interface DashboardStats {
  townCount: number;
  areaCount: number;
  listingCount: number;
  leadCount: number;
  blogCount: number;
  publishedBlogCount: number;
  scheduledBlogCount: number;
  loanOfficerCount: number;
  adCount: number;
}

interface GenerationJobSummary {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, selectedSite, isGlobalAdmin, loading: globalAdminLoading, checked: globalAdminChecked } = useGlobalAdmin();
  const { site, isWizardComplete, loading: siteAdminLoading } = useSiteAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({ 
    townCount: 0, areaCount: 0, listingCount: 0, leadCount: 0, 
    blogCount: 0, publishedBlogCount: 0, scheduledBlogCount: 0, 
    loanOfficerCount: 0, adCount: 0 
  });
  const [jobSummary, setJobSummary] = useState<GenerationJobSummary>({ pending: 0, processing: 0, completed: 0, failed: 0 });
  const [siteDetails, setSiteDetails] = useState<{ status: string; created_at: string; primary_domain: string | null } | null>(null);
  const [loading, setLoading] = useState(true);

  // Determine effective site ID based on role
  const effectiveSiteId = isGlobalAdmin ? selectedSiteId : site?.id;
  const effectiveSite = isGlobalAdmin ? selectedSite : site;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (effectiveSiteId && globalAdminChecked) {
      fetchSiteData();
    }
  }, [effectiveSiteId, globalAdminChecked]);

  const fetchSiteData = async () => {
    if (!effectiveSiteId) return;
    
    setLoading(true);
    try {
      // Get site details
      const { data: siteData } = await supabase
        .from('sites')
        .select('status, created_at, primary_domain')
        .eq('id', effectiveSiteId)
        .single();

      if (siteData) {
        setSiteDetails(siteData);
      }

      // Fetch stats in parallel
      const now = new Date().toISOString();
      const [townsRes, areasRes, listingsRes, leadsRes, blogRes, publishedBlogRes, scheduledBlogRes, loanOfficersRes, adsRes, jobsRes] = await Promise.all([
        supabase.from('towns').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId).eq('is_published', true),
        supabase.from('areas').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId).eq('is_published', true),
        supabase.from('listings').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId),
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId).eq('is_published', true).lte('published_at', now),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId).eq('is_published', true).gt('published_at', now),
        supabase.from('loan_officer_coverage')
          .select('loan_officer_id, towns!inner(site_id)')
          .eq('towns.site_id', effectiveSiteId),
        supabase.from('site_advertisements').select('id', { count: 'exact', head: true }).eq('site_id', effectiveSiteId),
        supabase.from('generation_jobs').select('status').eq('site_id', effectiveSiteId),
      ]);

      // Count distinct loan officers
      const distinctLoanOfficerIds = new Set(
        (loanOfficersRes.data || []).map((c: { loan_officer_id: string }) => c.loan_officer_id)
      );

      // Summarize generation jobs
      const jobs = jobsRes.data || [];
      const summary = { pending: 0, processing: 0, completed: 0, failed: 0 };
      jobs.forEach((j: { status: string }) => {
        if (j.status in summary) {
          summary[j.status as keyof GenerationJobSummary]++;
        }
      });

      setStats({
        townCount: townsRes.count || 0,
        areaCount: areasRes.count || 0,
        listingCount: listingsRes.count || 0,
        leadCount: leadsRes.count || 0,
        blogCount: blogRes.count || 0,
        publishedBlogCount: publishedBlogRes.count || 0,
        scheduledBlogCount: scheduledBlogRes.count || 0,
        loanOfficerCount: distinctLoanOfficerIds.size,
        adCount: adsRes.count || 0,
      });
      setJobSummary(summary);
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isLoading = authLoading || globalAdminLoading || siteAdminLoading || !globalAdminChecked || loading;

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return null;
  }

  const previewUrl = siteDetails?.primary_domain 
    ? `https://${siteDetails.primary_domain}` 
    : window.location.origin;

  return (
    <AdminLayout title="Dashboard">
      {/* Onboarding Progress Banner */}
      {effectiveSiteId && <OnboardingBanner siteId={effectiveSiteId} />}
      
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold">Welcome back!</h1>
        <p className="text-muted-foreground">
          Manage your real estate website for {effectiveSite?.location_served}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/towns')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Towns</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.townCount}</div>
            <p className="text-xs text-muted-foreground">Published</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/areas')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Areas</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.areaCount}</div>
            <p className="text-xs text-muted-foreground">Published</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/blog')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Blog</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.blogCount}</div>
            <p className="text-xs text-muted-foreground">
              {stats.publishedBlogCount} live
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/ads')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Ads</CardTitle>
            <Image className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.adCount}</div>
            <p className="text-xs text-muted-foreground">Configured</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/admin/leads')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.leadCount}</div>
            <p className="text-xs text-muted-foreground">Submissions</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {!isGlobalAdmin && !isWizardComplete && (
              <Button 
                variant="default"
                className="h-auto py-4 flex flex-col items-center gap-2 col-span-2"
                onClick={() => navigate('/agent/wizard')}
              >
                <Wand2 className="h-6 w-6" />
                <span>Continue Setup Wizard</span>
              </Button>
            )}
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/pages/home')}
            >
              <Home className="h-5 w-5" />
              <span className="text-xs">Homepage</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/profile')}
            >
              <User className="h-5 w-5" />
              <span className="text-xs">Profile</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/settings')}
            >
              <Settings className="h-5 w-5" />
              <span className="text-xs">Settings</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.open(previewUrl, '_blank')}
            >
              <ExternalLink className="h-5 w-5" />
              <span className="text-xs">View Site</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin/loan-officers')}
            >
              <Landmark className="h-5 w-5" />
              <span className="text-xs">Loan Officers</span>
            </Button>
          </CardContent>
        </Card>

        {/* Site Status */}
        <Card>
          <CardHeader>
            <CardTitle>Site Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={siteDetails?.status === 'active' ? 'default' : 'secondary'}>
                {siteDetails?.status || 'pending'}
              </Badge>
            </div>
            
            {siteDetails?.primary_domain && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Domain</span>
                <span className="text-sm font-medium">{siteDetails.primary_domain}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Created</span>
              <span className="text-sm">
                {siteDetails?.created_at ? new Date(siteDetails.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>

            {/* Generation Jobs Summary */}
            {(jobSummary.pending > 0 || jobSummary.processing > 0) && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium mb-2">Content Generation</p>
                <div className="flex gap-2 flex-wrap">
                  {jobSummary.processing > 0 && (
                    <Badge variant="secondary">{jobSummary.processing} processing</Badge>
                  )}
                  {jobSummary.pending > 0 && (
                    <Badge variant="outline">{jobSummary.pending} pending</Badge>
                  )}
                  {jobSummary.failed > 0 && (
                    <Badge variant="destructive">{jobSummary.failed} failed</Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Notifications */}
      {effectiveSiteId && (
        <div className="mt-6">
          <LeadNotificationSettings siteId={effectiveSiteId} />
        </div>
      )}
    </AdminLayout>
  );
}